import { useEffect, useState } from "react";

import { ChangeRequest as Base } from "@corca/tiptap";
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";

import { getUndoManager } from "@/components/tiptap/Collaboration";

import { ChangeRequestWidget } from "./ChangeRequestWidget";
import {
  applyChangeRequestContent,
  collectChangeRequests,
  findChangeRequest,
  getChangeRequestPendingAt,
  normalizeChangeRequest,
} from "./helpers";
import { useChangeRequestPending } from "./useChangeRequestPending";

import type { CommandProps } from "@tiptap/core";
import type { NodeViewProps } from "@tiptap/react";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    changeRequest: {
      approveChangeRequest: (id: string) => ReturnType;
      rejectChangeRequest: (id: string) => ReturnType;
      approveAllChangeRequests: () => ReturnType;
      rejectAllChangeRequests: () => ReturnType;
    };
  }
}

function ChangeRequestView({ editor, node }: NodeViewProps) {
  const changeRequestId = node.attrs.changeRequestId as string | undefined;
  const author = node.attrs.author as string | undefined;
  const changeRequest = normalizeChangeRequest(node);
  const hasAfter = !!changeRequest?.after;
  const pending = useChangeRequestPending(getChangeRequestPendingAt(node));
  const [isNew, setIsNew] = useState(() => editor.isInitialized);

  useEffect(() => {
    if (!isNew) return;
    const timeout = setTimeout(() => setIsNew(false), 1_000);
    return () => clearTimeout(timeout);
  }, [isNew]);

  return (
    <NodeViewWrapper
      className="change-request-node relative max-w-content-width w-full mx-auto sm:max-w-full"
      data-newly-inserted={isNew ? changeRequestId : undefined}
    >
      <div data-change-request-mode={changeRequest?.mode}>
        <NodeViewContent className="space-y-2" />
        {!hasAfter && (
          <ChangeRequestWidget
            author={author}
            pending={pending}
            onReject={() => {
              if (changeRequestId) {
                editor.chain().focus().rejectChangeRequest(changeRequestId).run();
              }
            }}
            onApprove={() => {
              if (changeRequestId) {
                editor.chain().focus().approveChangeRequest(changeRequestId).run();
              }
            }}
          />
        )}
      </div>
    </NodeViewWrapper>
  );
}

export const ChangeRequest = Base.extend<{
  lineMenu: "none";
}>({
  addOptions() {
    return {
      ...this.parent?.(),
      lineMenu: "none" as const,
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChangeRequestView);
  },

  addCommands() {
    return {
      approveChangeRequest:
        (id: string) =>
        ({ state, dispatch }: CommandProps) => {
          const cr = findChangeRequest(state, id);
          if (!cr) return false;
          const changeRequest = normalizeChangeRequest(cr.node);
          if (!changeRequest) return false;
          if (!dispatch) return true;

          const undoManager = getUndoManager(state);
          undoManager.stopCapturing();
          const { tr } = state;
          applyChangeRequestContent(tr, cr.from, cr.to, changeRequest.after);
          dispatch(tr);
          undoManager.stopCapturing();

          return true;
        },

      rejectChangeRequest:
        (id: string) =>
        ({ state, dispatch }: CommandProps) => {
          const cr = findChangeRequest(state, id);
          if (!cr) return false;
          const changeRequest = normalizeChangeRequest(cr.node);
          if (!changeRequest) return false;
          if (!dispatch) return true;

          const undoManager = getUndoManager(state);
          undoManager.stopCapturing();
          const { tr } = state;
          applyChangeRequestContent(tr, cr.from, cr.to, changeRequest.before);
          dispatch(tr);
          undoManager.stopCapturing();

          return true;
        },

      approveAllChangeRequests:
        () =>
        ({ state, dispatch }: CommandProps) => {
          const crs = collectChangeRequests(state);
          if (crs.length === 0) return false;
          if (!dispatch) return true;

          const undoManager = getUndoManager(state);
          undoManager.stopCapturing();
          const { tr } = state;
          for (let i = crs.length - 1; i >= 0; i--) {
            applyChangeRequestContent(tr, crs[i].from, crs[i].to, crs[i].changeRequest.after);
          }
          dispatch(tr);
          undoManager.stopCapturing();

          return true;
        },

      rejectAllChangeRequests:
        () =>
        ({ state, dispatch }: CommandProps) => {
          const crs = collectChangeRequests(state);
          if (crs.length === 0) return false;
          if (!dispatch) return true;

          const undoManager = getUndoManager(state);
          undoManager.stopCapturing();
          const { tr } = state;
          for (let i = crs.length - 1; i >= 0; i--) {
            applyChangeRequestContent(tr, crs[i].from, crs[i].to, crs[i].changeRequest.before);
          }
          dispatch(tr);
          undoManager.stopCapturing();

          return true;
        },
    };
  },
});
